import { useState } from 'react';
import { Users, UserPlus, Mail, Shield, Crown, Trash2, RefreshCw, Send, X, LayoutDashboard } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useFamily } from '@hooks/useFamily';
import { useAuth } from '@hooks/useAuth';
import PageHeader from '@components/layout/PageHeader';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import Badge from '@components/common/Badge';
import PageTutorial from '@components/common/PageTutorial';
import FamilyDashboard from '@components/family/FamilyDashboard';
import { formatDate } from '@utils/formatters';
import { clsx } from 'clsx';

const inviteSchema = z.object({
  email: z.string().email('E-mail inválido').min(1, 'E-mail obrigatório'),
  role: z.enum(['member', 'admin'], { required_error: 'Selecione uma função' }),
});

const ROLE_CONFIG = {
  owner: {
    label: 'Proprietário',
    variant: 'warning',
    icon: <Crown size={14} />,
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  },
  admin: {
    label: 'Administrador',
    variant: 'primary',
    icon: <Shield size={14} />,
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  },
  member: {
    label: 'Membro',
    variant: 'default',
    icon: <Users size={14} />,
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  },
};

const getInitials = (name = '', email = '') => {
  if (name) {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  }
  return email.slice(0, 2).toUpperCase();
};

const MemberCard = ({ member, isCurrentUser, canManage, isOwner, onRemove, onChangeRole, onTransfer }) => {
  const role = member.role || 'member';
  const roleConfig = ROLE_CONFIG[role] || ROLE_CONFIG.member;
  const [showActions, setShowActions] = useState(false);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 flex items-start gap-4">
      {/* Avatar */}
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
      >
        {member.avatar ? (
          <img src={member.avatar} alt={member.name} className="w-full h-full rounded-full object-cover" />
        ) : (
          getInitials(member.name, member.email)
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-gray-900 dark:text-white truncate">
            {member.name || member.email}
            {isCurrentUser && <span className="ml-1 text-xs text-gray-400">(você)</span>}
          </p>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${roleConfig.className}`}>
            {roleConfig.icon}
            {roleConfig.label}
          </span>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{member.email}</p>
        {member.joinedAt && (
          <p className="text-xs text-gray-400 mt-1">Membro desde {formatDate(member.joinedAt)}</p>
        )}
      </div>

      {/* Actions */}
      {canManage && !isCurrentUser && role !== 'owner' && (
        <div className="flex-shrink-0 relative">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowActions(!showActions)}
          >
            Ações
          </Button>
          {showActions && (
            <div
              className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg z-10"
              onMouseLeave={() => setShowActions(false)}
            >
              {isOwner && role === 'member' && (
                <button
                  className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 rounded-t-xl"
                  onClick={() => { onChangeRole(member.userId, 'admin'); setShowActions(false); }}
                >
                  <Shield size={14} /> Promover a Admin
                </button>
              )}
              {isOwner && role === 'admin' && (
                <button
                  className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                  onClick={() => { onChangeRole(member.userId, 'member'); setShowActions(false); }}
                >
                  <Users size={14} /> Rebaixar a Membro
                </button>
              )}
              {isOwner && (
                <button
                  className="w-full text-left px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2"
                  onClick={() => { onTransfer(member); setShowActions(false); }}
                >
                  <Crown size={14} /> Transferir Propriedade
                </button>
              )}
              <button
                className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 rounded-b-xl"
                onClick={() => { onRemove(member); setShowActions(false); }}
              >
                <Trash2 size={14} /> Remover Membro
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const InviteCard = ({ invite, onCancel, onResend, isCanceling, isResending }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 border-dashed shadow-sm p-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
        <Mail size={18} className="text-gray-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-700 dark:text-gray-300 truncate">{invite.email}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-amber-600 dark:text-amber-400 font-medium bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
            Convite pendente
          </span>
          {invite.role && (
            <span className="text-xs text-gray-500">· {ROLE_CONFIG[invite.role]?.label || invite.role}</span>
          )}
        </div>
        {invite.expiresAt && (
          <p className="text-xs text-gray-400 mt-0.5">Expira em {formatDate(invite.expiresAt)}</p>
        )}
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<RefreshCw size={13} />}
          onClick={() => onResend(invite.inviteId)}
          isLoading={isResending}
        >
          Reenviar
        </Button>
        <Button
          size="sm"
          variant="danger"
          leftIcon={<X size={13} />}
          onClick={() => onCancel(invite.inviteId)}
          isLoading={isCanceling}
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
};

const Family = () => {
  const { user, tenant } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const {
    members,
    invites,
    isLoadingMembers,
    isLoadingInvites,
    inviteMember,
    cancelInvite,
    resendInvite,
    removeMember,
    updateMemberRole,
    transferOwnership,
    isInviting,
    isCanceling,
    isResending,
    isRemoving,
  } = useFamily();

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [confirmTransfer, setConfirmTransfer] = useState(null);

  const currentUserMember = members.find((m) => m.userId === user?.userId || m.email === user?.email);
  const isOwner = currentUserMember?.role === 'owner';
  const isAdmin = currentUserMember?.role === 'admin' || isOwner;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', role: 'member' },
  });

  const onSubmitInvite = async (data) => {
    await inviteMember(data);
    reset();
    setIsInviteModalOpen(false);
  };

  const handleRemove = async () => {
    if (confirmRemove) {
      await removeMember(confirmRemove.userId);
      setConfirmRemove(null);
    }
  };

  const handleTransfer = async () => {
    if (confirmTransfer) {
      await transferOwnership(confirmTransfer.userId);
      setConfirmTransfer(null);
    }
  };

  const handleChangeRole = (userId, role) => {
    updateMemberRole({ userId, role });
  };

  if (isLoadingMembers) {
    return <Loading text="Carregando família..." />;
  }

  const otherMembers = members.filter((m) => m.userId !== user?.userId && m.email !== user?.email);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Financas em Familia"
        subtitle="Gerencie os membros e convites do seu plano familia"
        action={
          isAdmin && (
            <Button leftIcon={<UserPlus size={18} />} onClick={() => setIsInviteModalOpen(true)}>
              Convidar Membro
            </Button>
          )
        }
      />

      <PageTutorial
        pageId="family"
        title="Gestao familiar"
        steps={[
          'Convide ate 4 membros para compartilhar o controle financeiro (5 usuarios no total)',
          'O Dashboard mostra a visao consolidada de todos os membros',
          'Defina papeis (admin/membro) para controlar permissoes',
        ]}
      />

      {/* Tabs */}
      {tenant?.plan === 'family' && members.length > 1 && (
        <div className="flex items-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-1 w-fit">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={16} /> },
            { id: 'members', label: 'Membros', icon: <Users size={16} /> },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Family Dashboard Tab */}
      {activeTab === 'dashboard' && tenant?.plan === 'family' && members.length > 1 && (
        <FamilyDashboard />
      )}

      {/* Members Tab */}
      {(activeTab === 'members' || !tenant?.plan || tenant?.plan !== 'family' || members.length <= 1) && (
      <>
      {/* Plano Banner - mostrar se nao for plano familia */}
      {currentUserMember && tenant?.plan !== 'family' && (
        <div className="bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl p-5 text-white flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Crown size={20} />
            </div>
            <div>
              <p className="font-semibold">Plano Família necessário</p>
              <p className="text-sm text-white/80">
                Convide até 4 membros da família para gerenciar finanças juntos.
              </p>
            </div>
          </div>
          <Button variant="secondary" className="flex-shrink-0 bg-white text-primary-700 hover:bg-white/90 border-0">
            Fazer Upgrade
          </Button>
        </div>
      )}

      {/* Membros */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Users size={20} className="text-primary-600" />
            Membros ({members.length})
          </h2>
        </div>

        {members.length === 0 ? (
          <EmptyState
            icon={<Users size={32} />}
            title="Nenhum membro ainda"
            description="Convide membros da sua família para gerenciarem as finanças juntos."
            action={isAdmin ? () => setIsInviteModalOpen(true) : undefined}
            actionLabel="Convidar Membro"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current user first */}
            {currentUserMember && (
              <MemberCard
                key={currentUserMember.userId}
                member={currentUserMember}
                isCurrentUser={true}
                canManage={isAdmin}
                isOwner={isOwner}
                onRemove={setConfirmRemove}
                onChangeRole={handleChangeRole}
                onTransfer={setConfirmTransfer}
              />
            )}
            {otherMembers.map((member) => (
              <MemberCard
                key={member.userId}
                member={member}
                isCurrentUser={false}
                canManage={isAdmin}
                isOwner={isOwner}
                onRemove={setConfirmRemove}
                onChangeRole={handleChangeRole}
                onTransfer={setConfirmTransfer}
              />
            ))}
          </div>
        )}
      </div>

      {/* Convites pendentes */}
      {(isAdmin || invites.length > 0) && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Mail size={20} className="text-amber-500" />
              Convites Pendentes ({invites.length})
            </h2>
          </div>

          {isLoadingInvites ? (
            <Loading text="Carregando convites..." />
          ) : invites.length === 0 ? (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center">
              <Mail size={24} className="mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum convite pendente</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invites.map((invite) => (
                <InviteCard
                  key={invite.inviteId}
                  invite={invite}
                  onCancel={cancelInvite}
                  onResend={resendInvite}
                  isCanceling={isCanceling}
                  isResending={isResending}
                />
              ))}
            </div>
          )}
        </div>
      )}

      </>
      )}

      {/* Invite Modal */}
      <Modal
        isOpen={isInviteModalOpen}
        onClose={() => { setIsInviteModalOpen(false); reset(); }}
        title="Convidar Membro"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setIsInviteModalOpen(false); reset(); }}>
              Cancelar
            </Button>
            <Button
              leftIcon={<Send size={16} />}
              onClick={handleSubmit(onSubmitInvite)}
              isLoading={isInviting}
            >
              Enviar Convite
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              E-mail do convidado
            </label>
            <input
              type="email"
              placeholder="email@exemplo.com"
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Função
            </label>
            <select
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              {...register('role')}
            >
              <option value="member">Membro — pode ver e adicionar transações</option>
              <option value="admin">Administrador — pode gerenciar tudo</option>
            </select>
            {errors.role && (
              <p className="text-xs text-red-500 mt-1">{errors.role.message}</p>
            )}
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              O convidado receberá um e-mail com instruções para aceitar o convite e acessar a conta família.
            </p>
          </div>
        </div>
      </Modal>

      {/* Confirm Remove Modal */}
      <Modal
        isOpen={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        title="Remover Membro"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmRemove(null)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleRemove} isLoading={isRemoving}>
              Remover
            </Button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-gray-400">
          Tem certeza que deseja remover{' '}
          <strong>{confirmRemove?.name || confirmRemove?.email}</strong> do grupo família?
          Ele perderá o acesso imediatamente.
        </p>
      </Modal>

      {/* Confirm Transfer Ownership Modal */}
      <Modal
        isOpen={!!confirmTransfer}
        onClose={() => setConfirmTransfer(null)}
        title="Transferir Propriedade"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmTransfer(null)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleTransfer}>
              Transferir
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-gray-600 dark:text-gray-400">
            Tem certeza que deseja transferir a propriedade para{' '}
            <strong>{confirmTransfer?.name || confirmTransfer?.email}</strong>?
          </p>
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3">
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Atenção: você perderá os privilégios de proprietário e não poderá desfazer esta ação.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Family;
